import React, { useState, useEffect } from 'react';
import { TiVolumeMute } from 'react-icons/ti';
import { PiWaveformFill, PiPlantFill, PiBrainFill } from 'react-icons/pi';
import { HiMiniBellAlert } from 'react-icons/hi2';
import { FaYoutube } from 'react-icons/fa';
import { BsInfoCircle } from 'react-icons/bs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { SessionData } from './types';
import CustomMusicModal from './CustomMusicModal';
import ProjectSelector, { ProjectSelectorHandle } from './ProjectSelector';
import { useDebounce } from '../../../hooks/useDebounce';



interface SoloModeContentProps {
  sessionData: SessionData;
  setSessionData: React.Dispatch<React.SetStateAction<SessionData>>;
  onStartSession: () => void;
  onManageProjects?: () => void;
  onCreateProjects?: () => void;
  projectSelectorRef?: React.RefObject<ProjectSelectorHandle | null>;
}

const SoloModeContent: React.FC<SoloModeContentProps> = ({
  sessionData,
  setSessionData,
  onStartSession,
  onManageProjects,
  onCreateProjects,
  projectSelectorRef
}) => {
  const [showCustomDuration, setShowCustomDuration] = useState(false);
  const [showCustomMusicModal, setShowCustomMusicModal] = useState(false);
  const [localCustomDuration, setLocalCustomDuration] = useState(sessionData.customDuration || '');

  // Sync local custom duration when prop changes (for external updates)
  useEffect(() => {
    setLocalCustomDuration(sessionData.customDuration || '');
  }, [sessionData.customDuration]);

  const debouncedCustomDuration = useDebounce(localCustomDuration, 500);

  // Update session data when debounced value changes
  useEffect(() => {
    if (debouncedCustomDuration !== (sessionData.customDuration || '')) {
      if (debouncedCustomDuration === '' || /^\d+$/.test(debouncedCustomDuration)) {
        setSessionData(prev => ({
          ...prev,
          customDuration: debouncedCustomDuration,
          duration: debouncedCustomDuration ? parseInt(debouncedCustomDuration) : 0
        }));
      }
    }
  }, [debouncedCustomDuration, setSessionData]);

  // Auto-manage break reminders based on duration
  useEffect(() => {
    if (sessionData.duration <= 25) {
      if (sessionData.breakReminders) {
        setSessionData(prev => ({ ...prev, breakReminders: false }));
      }
    } else {
      if (!sessionData.breakReminders) {
        setSessionData(prev => ({ ...prev, breakReminders: true }));
      }
    }
  }, [sessionData.duration, setSessionData]); // Only auto-update when duration changes to allow manual toggle

  const handleCustomMusicSave = (youtubeUrl: string) => {
    setSessionData({ ...sessionData, music: 'custom', customMusicURL: youtubeUrl });
  };

  const handleRemoveCustomMusic = () => {
    setSessionData({ ...sessionData, music: 'silence', customMusicURL: undefined });
  };

  return (
    <div className="w-full min-h-screen bg-[#FFF8F0] pt-2 sm:pt-4 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto space-y-8 sm:space-y-10">
        {/* Session Title */}
        <div className="text-center space-y-3">
          <h1 className="text-4xl sm:text-5xl font-medium text-gray-900">
            Solo Focus Session
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 font-light">
            A quiet space for focused work
          </p>
        </div>

        {/* Project Selector */}
        <div className="space-y-4">
          <ProjectSelector
            ref={projectSelectorRef}
            selectedProjectId={sessionData.projectId}
            onProjectChange={(projectId) => setSessionData({ ...sessionData, projectId })}
            showManageButton={true}
            onManageClick={onManageProjects}
            onCreateClick={onCreateProjects}
            required={true}
          />
        </div>

        {/* Primary Input - What to work on */}
        <div className="space-y-4">
          <label className="block text-lg text-gray-700 font-normal">
            What will you focus on?
          </label>
          <input
            type="text"
            value={sessionData.goal}
            onChange={(e) => setSessionData({ ...sessionData, goal: e.target.value })}
            placeholder="e.g. Finish today’s top task , Prepare meeting notes"
            className="w-full px-6 py-5 bg-white border border-gray-200/60 rounded-2xl text-gray-900 placeholder:text-gray-400 focus:ring-1 focus:ring-orange-400/30 focus:border-orange-300/50 focus:outline-none transition-all duration-300 text-lg font-normal"
            autoFocus
          />
        </div>

        {/* Session Duration */}
        <div className="space-y-4">
          <label className="block text-lg text-gray-700 font-normal">
            Focus Duration
          </label>
          <div className="flex flex-wrap items-center gap-3">
            {[25, 45, 60].map((duration) => (
              <button
                key={duration}
                onClick={() => {
                  setSessionData({ ...sessionData, duration, customDuration: '' });
                  setShowCustomDuration(false);
                }}
                className={`px-7 py-3.5 rounded-full font-normal transition-all duration-300 text-base ${sessionData.duration === duration && !showCustomDuration
                  ? 'bg-orange-50 text-orange-600 border border-orange-200/40'
                  : 'bg-white text-gray-600 border border-gray-200/40 hover:border-orange-200/60 hover:text-orange-600/80'
                  }`}
              >
                {duration}m
              </button>
            ))}
            {!showCustomDuration ? (
              <button
                onClick={() => {
                  setShowCustomDuration(true);
                  setLocalCustomDuration('');
                  setSessionData({ ...sessionData, customDuration: '', duration: 0 });
                }}
                className="px-4 py-3.5 text-sm text-gray-500 hover:text-orange-600 transition-colors font-normal"
              >
                + Custom time
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={localCustomDuration}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '' || /^\d+$/.test(value)) {
                      setLocalCustomDuration(value);
                    }
                  }}
                  placeholder="Minutes"
                  className="px-5 py-3.5 rounded-full border-2 border-orange-300 text-gray-900 bg-white placeholder:text-gray-400 focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 focus:outline-none text-base transition-all duration-300 w-28 text-center font-normal shadow-sm"
                  autoFocus
                />
                <button
                  onClick={() => {
                    setShowCustomDuration(false);
                    setSessionData({ ...sessionData, customDuration: '', duration: 25 });
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors font-normal"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Optional Focus Support */}
        <div className="space-y-6 pt-6 border-t border-gray-200/40">
          {/* Focus Sounds */}
          <div className="space-y-3">
            <label className="block text-lg text-gray-700 font-normal">Focus sounds</label>
            <div className="flex flex-wrap gap-2.5">
              {[
                { id: 'silence', icon: <TiVolumeMute className="text-base" />, label: 'Silence' },
                { id: 'noise', icon: <PiWaveformFill className="text-base" />, label: 'Noise' },
                { id: 'nature', icon: <PiPlantFill className="text-base" />, label: 'Nature' },
                { id: 'binaural', icon: <PiBrainFill className="text-base" />, label: 'Binaural' }
              ].map((option) => (
                <button
                  key={option.id}
                  onClick={() => setSessionData({ ...sessionData, music: option.id })}
                  className={`px-4 py-2.5 rounded-full font-normal transition-all duration-300 text-sm flex items-center gap-1.5 ${sessionData.music === option.id
                    ? 'bg-orange-50/40 text-orange-600/90 border border-orange-200/30'
                    : 'bg-white text-gray-500 border border-gray-200/40 hover:border-orange-200/50 hover:text-gray-700'
                    }`}
                >
                  <span className="opacity-70">{option.icon}</span>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>

            {/* Custom Music Button */}
            <div className="mt-3">
              {sessionData.music === 'custom' && sessionData.customMusicURL ? (
                <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-orange-200/40 rounded-full text-sm max-w-full">
                  <FaYoutube className="text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700 truncate max-w-[150px] sm:max-w-xs font-normal">{sessionData.customMusicURL}</span>
                  <button
                    onClick={handleRemoveCustomMusic}
                    className="ml-1 text-xs text-gray-400 hover:text-gray-600 transition-colors font-normal px-2 py-0.5 hover:bg-gray-50 rounded flex-shrink-0"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowCustomMusicModal(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-dashed border-orange-200/60 hover:border-orange-300/80 rounded-full text-sm text-gray-700 hover:text-orange-600 transition-all duration-300 font-normal group"
                >
                  <FaYoutube className="text-orange-500/70 group-hover:text-orange-600 transition-colors" />
                  <span>Use your own focus music</span>
                </button>
              )}
            </div>
          </div>

          {/* Break Reminders */}
          <div className="bg-white border border-gray-200/40 rounded-2xl p-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <HiMiniBellAlert className="text-orange-500/70 text-base" />
                  <h4 className="text-sm font-normal text-gray-600">Gentle check-ins</h4>
                </div>
                <p className="text-xs text-gray-400 font-light leading-relaxed">
                  Soft reminders to breathe, relax posture, and stay present
                  <TooltipProvider>
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <span className="inline-block ml-1 align-middle cursor-help">
                          <BsInfoCircle className="text-gray-400 hover:text-orange-500 transition-colors text-xs" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs text-justify">
                          Gentle nudges to breathe, adjust posture, or reset attention — without breaking your focus.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </p>
              </div>
              <button
                onClick={() => setSessionData({ ...sessionData, breakReminders: !sessionData.breakReminders })}
                disabled={sessionData.duration <= 25}
                className={`relative w-11 h-6 rounded-full transition-all duration-300 flex-shrink-0 ${sessionData.breakReminders ? 'bg-orange-500' : 'bg-gray-300/60'} ${sessionData.duration <= 25 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-300 ${sessionData.breakReminders ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Custom Music Modal */}
        <CustomMusicModal
          isOpen={showCustomMusicModal}
          onClose={() => setShowCustomMusicModal(false)}
          onSave={handleCustomMusicSave}
          currentUrl={sessionData.customMusicURL || ''}
        />

        {/* Primary CTA */}
        <div className="pt-8 text-center space-y-3">
          <button
            onClick={onStartSession}
            disabled={sessionData.duration < 1}
            className={`px-12 py-4 rounded-2xl font-medium text-lg transition-all duration-300 ${sessionData.duration < 1
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-orange-600 hover:bg-orange-700 text-white'
              }`}
          >
            Start Focus Session
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoloModeContent;
